import { CircularProgress } from "@mui/material";
import React from "react";

import { AutopilotUserUsage } from "services/AutopilotService";

import UsageSection, { UsageSummaryItem } from "./UsageSection";
import { UsageTableRow } from "./UsageTable";
import styles from "../ProfilePage.module.scss";

const sum = <T,>(items: T[], pick: (item: T) => number) =>
  items.reduce((total, item) => total + pick(item), 0);

interface UsageTabProps {
  usage: AutopilotUserUsage | null;
  loading: boolean;
}

const UsageTab: React.FC<UsageTabProps> = ({ usage, loading }) => {
  if (loading) {
    return (
      <div className={styles.developerCenter}>
        <CircularProgress size={28} sx={{ color: "#bdb8ff" }} />
      </div>
    );
  }

  if (!usage) {
    return (
      <p className={styles.developerEmptyText}>No usage data available.</p>
    );
  }

  const { weekly_usage_by_service: weekly, monthly_usage: monthly } = usage;

  const weeklyFrames = sum(weekly, (r) => r.frames_processed);
  const weeklySummary: UsageSummaryItem[] = [
    { label: "Frames processed", value: weeklyFrames.toLocaleString() },
    {
      label: "Assets processed",
      value: sum(weekly, (r) => r.assets_processed).toLocaleString(),
    },
    {
      label: "Inferences executed",
      value: sum(weekly, (r) => r.inferences_executed).toLocaleString(),
    },
  ];
  const weeklyRows: UsageTableRow[] = weekly.map((s) => ({
    key: s.service,
    cells: [
      s.service,
      s.frames_processed.toLocaleString(),
      s.assets_processed.toLocaleString(),
      s.inferences_executed.toLocaleString(),
    ],
  }));

  const monthlySummary: UsageSummaryItem[] = [
    {
      label: "Frames processed",
      value: sum(monthly, (m) => m.frames_processed).toLocaleString(),
    },
    {
      label: "Assets processed",
      value: sum(monthly, (m) => m.assets_processed).toLocaleString(),
    },
    {
      label: "Inferences executed",
      value: sum(monthly, (m) => m.inferences_executed).toLocaleString(),
    },
  ];
  const monthlyRows: UsageTableRow[] = monthly.map((m) => ({
    key: m.month,
    cells: [
      m.month,
      m.frames_processed.toLocaleString(),
      m.assets_processed.toLocaleString(),
      m.inferences_executed.toLocaleString(),
    ],
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "48px" }}>
      <UsageSection
        title="Last 7 days usage"
        summary={weeklySummary}
        columns={[
          "Service",
          "Frames Processed",
          "Assets Processed",
          "Inferences Executed",
        ]}
        rows={weeklyRows}
        emptyText="No usage in the last 7 days."
      />
      <UsageSection
        title="Last 6 months usage"
        summary={monthlySummary}
        columns={[
          "Month",
          "Frames Processed",
          "Assets Processed",
          "Inferences Executed",
        ]}
        rows={monthlyRows}
        emptyText="No monthly usage data available."
      />
    </div>
  );
};

export default UsageTab;
